<template>
    <b-container fluid class="mt-3">
        <div v-if="currentPortal == null" class="text-center">
            <b-spinner label="Spinning"></b-spinner>
        </div>
        <b-row v-if="currentPortal">
            <b-col md="12">
                <b-row class="mb-2">
                    <b-col md="6">
                        <span class="align-middle"><b-iconstack font-scale="1.5" class="h-100">
                            <b-icon stacked icon="circle-fill"
                                    :variant="currentPortal.disabled ? 'secondary' : currentPortal.published ? 'success' : 'warning'"></b-icon>
                            <b-icon stacked icon="power" scale="0.5" variant="white"></b-icon>
                        </b-iconstack></span>
                        <span class="h4 align-middle">
                            Portal</span>
                    </b-col>

                    <b-col md="6" class="text-right">
                        <b-dropdown id="portal-dropdown" split right split-variant="outline-primary"
                                    variant="primary" class="mr-2" v-if="!isNewPortal"
                                    :split-to="{ name: currentPortal.published ? 'PortalSubmission' : 'DraftSubmission', params: { name: currentPortal.name } }">
                            <template v-slot:button-content>
                                <b-icon icon="eye-fill"></b-icon>
                                Show
                            </template>
                            <b-dropdown-item @click="duplicatePortal">
                                <b-icon icon="files"></b-icon>
                                Duplicate
                            </b-dropdown-item>
                            <b-dropdown-item-button @click="clearPortalCache(currentPortal.name)">
                                <b-icon icon="arrow-repeat"></b-icon>
                                Clear cache
                            </b-dropdown-item-button>
                            <b-dropdown-item v-b-modal.modal-iframe>
                                <b-icon icon="window"></b-icon>
                                Iframe
                            </b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item @click="showExportModal">
                                <b-icon icon="box-arrow-right"></b-icon>
                                Export
                            </b-dropdown-item>
                            <b-dropdown-item v-if="isManager || isAdmin" @click="showImportModal">
                                <b-icon icon="box-arrow-in-left"></b-icon>
                                Import from file
                            </b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item v-if="isManager || isAdmin"
                                             :variant="currentPortal.disabled ? 'success' : 'warning'"
                                             v-b-modal.modal-disable>
                                <b-icon icon="power"></b-icon>
                                {{ currentPortal.disabled ? 'Enable' : 'Disable' }}
                            </b-dropdown-item>
                        </b-dropdown>

                        <b-avatar button v-b-modal.manageModal :variant="isManager ? 'primary' : 'secondary'"
                                  icon="people"
                                  class="align-center"></b-avatar>
                    </b-col>
                </b-row>

                <b-alert v-if="currentPortal.disabled" show variant="secondary">
                    <b-icon icon="exclamation-circle-fill" variant="secondary" class="mr-1"></b-icon>This portal is disabled</b-alert>
                <b-alert v-if="!currentPortal.published" show variant="warning">
                    <b-icon icon="exclamation-triangle-fill" variant="warning" class="mr-1"></b-icon>This portal is a draft (unpublished)</b-alert>

                <b-form @submit="onSubmit">

                    <div role="tablist">
                        <b-card no-body class="mb-1">
                            <b-card-header header-tag="header" class="p-1" role="tab">
                                <b-button block v-b-toggle.acc-main-conf variant="primary">Main configuration</b-button>
                            </b-card-header>
                            <b-collapse id="acc-main-conf" visible accordion="my-accordion" role="tabpanel">
                                <b-card-body>
                                    <!-- Main configuration -->
                                    <b-form-row class="mb-2">
                                        <b-col sm="12" md="8" lg="9">
                                            <b-form-row>
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-group label="Client" label-for="client" class="required">
                                                        <b-form-select v-if="clients" id="client"
                                                                       name="client"
                                                                       @change="selectClient"
                                                                       v-model="currentPortal.client" required>
                                                            <b-form-select-option v-for="selectClient in clients"
                                                                                  :key="selectClient.id"
                                                                                  :value="selectClient">
                                                                {{ selectClient.name }}
                                                            </b-form-select-option>
                                                        </b-form-select>
                                                        <b-input-group v-if="!clients">
                                                            <b-form-input id="client" name="client"
                                                                          v-model="currentPortal.client.name" disabled
                                                                          required/>
                                                            <b-input-group-append is-text>
                                                                <b-link @click="getClients" class="text-primary"
                                                                        v-b-tooltip.hover>
                                                                    <b-icon icon="pencil"></b-icon>
                                                                </b-link>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-group class="required">
                                                        <label for="portalName">Name
                                                            <b-link id="portalNameHelp">
                                                                <b-icon icon="question-circle"
                                                                        variant="primary"></b-icon>
                                                            </b-link>
                                                            <b-popover target="portalNameHelp" triggers="focus"
                                                                       placement="top">
                                                                Has to be unique, without spaces.<br>
                                                                Used to access the portal.
                                                            </b-popover>
                                                        </label>
                                                        <b-form-input id="portalName" name="portalName"
                                                                      v-model="currentPortal.name" required
                                                                      :formatter="stringFormatter" @update="isNewPortal ? nameChanged = false : nameChanged = true"/>
                                                        <b-form-invalid-feedback :state="!nameChanged">
                                                            <b-icon icon="exclamation-triangle" variant="error"></b-icon>
                                                            Changing the name also change the URL of the portal.
                                                        </b-form-invalid-feedback>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-group class="required">
                                                        <label for="className">Classname
                                                            <b-link id="classNameHelp">
                                                                <b-icon icon="question-circle"
                                                                        variant="primary"></b-icon>
                                                            </b-link>
                                                            <b-popover target="classNameHelp" triggers="focus"
                                                                       placement="top">
                                                                The opportunity object submitted.<br>
                                                                Default is Submission.
                                                            </b-popover>
                                                        </label>
                                                        <b-form-select id="className" name="className"
                                                                       v-model="currentPortal.className"
                                                                       :options="classNames" required
                                                                        @change="selectClass"/>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-group label="Company Link" label-for="companyLink">
                                                        <b-form-input id="companyLink" name="companyLink"
                                                                      v-model="currentPortal.companyLink"/>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-group label="Title" label-for="portalTitle">
                                                        <b-form-input id="portalTitle" name="portalTitle"
                                                                      v-model="currentPortal.title"/>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-group class="required">
                                                        <label for="companySupportEmail">Support Email
                                                            <b-link id="companySupportEmailHelp">
                                                                <b-icon icon="question-circle"
                                                                        variant="primary"></b-icon>
                                                            </b-link>
                                                            <b-popover target="companySupportEmailHelp" triggers="focus"
                                                                       placement="top">
                                                                The support email displayed when an error occurs.<br>
                                                                Use the client support email.
                                                            </b-popover>
                                                        </label>
                                                        <b-form-input id="companySupportEmail"
                                                                      name="companySupportEmail"
                                                                      v-model="currentPortal.companySupportEmail"/>
                                                        <b-form-text id="companySupportEmail-feedback" text-variant="danger"
                                                                     v-if="currentPortal.companySupportEmail === 'support@inpart.io'">
                                                            <b-icon icon="exclamation-triangle" variant="error"></b-icon>
                                                            <span>This is Inpart support email and not a client support email.</span>
                                                        </b-form-text>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3" v-if="!currentPortal.leadSpace">
                                                    <b-form-group>
                                                        <label for="accessCode">Access Code Field
                                                            <b-link id="accessCodeHelp">
                                                                <b-icon icon="question-circle"
                                                                        variant="primary"></b-icon>
                                                            </b-link>
                                                            <b-popover target="accessCodeHelp" triggers="focus"
                                                                       placement="top">
                                                                The field name used in classic for the access code.<br>
                                                                Default is "text10"
                                                            </b-popover>
                                                        </label>
                                                        <b-form-input id="accessCode" name="accessCode"
                                                                      v-model="currentPortal.accessCode"/>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col sm="6" md="4" lg="3" v-if="currentPortal.leadSpace">
                                                    <b-form-group class="required">
                                                        <label for="brief">Initiative
                                                            <b-link id="briefHelp">
                                                                <b-icon icon="question-circle" variant="primary"></b-icon>
                                                            </b-link>
                                                            <b-popover target="briefHelp" triggers="focus"
                                                                       placement="top">
                                                                The Initiative attached to the lead.
                                                            </b-popover>
                                                        </label>
                                                        <b-input-group v-if="briefs || !currentPortal.brief" >
                                                            <b-form-select id="brief" name="brief" @change="selectBrief"
                                                                           v-model="selectedBrief" required>
                                                                <b-form-select-option v-for="selectBrief in briefs"
                                                                                      :key="selectBrief.dobjId"
                                                                                      :value="selectBrief.dobjId">
                                                                    {{ selectBrief.name }}
                                                                </b-form-select-option>
                                                            </b-form-select>
                                                            <b-input-group-append is-text>
                                                                <b-link @click="getBriefs" class="text-primary"
                                                                        v-b-tooltip.hover>
                                                                    <b-icon icon="cloud-download"></b-icon>
                                                                </b-link>
                                                            </b-input-group-append>
                                                        </b-input-group>

                                                        <b-input-group v-if="currentPortal.briefName && !briefs">
                                                            <b-form-input id="brief" name="brief"
                                                                          v-model="currentPortal.briefName" disabled
                                                                          required/>
                                                            <b-input-group-append is-text>
                                                                <b-link @click="getBriefs" class="text-primary"
                                                                        v-b-tooltip.hover>
                                                                    <b-icon icon="pencil"></b-icon>
                                                                </b-link>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                    </b-form-group>
                                                </b-col>
                                            </b-form-row>
                                            <b-form-row v-if="isAdmin">
                                                <b-col sm="6" md="4" lg="3">
                                                    <b-form-checkbox id="captchaCheck" name="captchaCheck"
                                                                     class="ckc-switch-square" switch
                                                                     v-model="currentPortal.captchaCheck">
                                                        Captcha Check
                                                    </b-form-checkbox>
                                                </b-col>
                                            </b-form-row>
                                        </b-col>
                                        <b-col sm="12" md="4" lg="3">
                                            <b-form-group class="required">
                                                <label for="selectAllowedExtensions">Allowed file extensions
                                                    <b-link id="selectAllowedExtensionsHelp">
                                                        <b-icon icon="question-circle" variant="primary"></b-icon>
                                                    </b-link>
                                                    <b-popover target="selectAllowedExtensionsHelp" triggers="focus"
                                                               placement="top">
                                                        List of allowed files extension to upload.<br>
                                                        You can manually edit the list to add custom extensions.
                                                    </b-popover>
                                                </label>
                                                <b-form-select id="selectAllowedExtensions"
                                                               name="selectAllowedExtensions"
                                                               v-model="selectedExtensions"
                                                               :options="extensions" multiple required
                                                               :disabled="extensionsEditMode"/>
                                            </b-form-group>

                                            <b-input-group>
                                                <b-form-input id="allowedExtensions" name="allowedExtensions"
                                                              v-model="currentPortal.allowedExtensions"
                                                              :disabled="!extensionsEditMode"/>
                                                <b-input-group-append is-text>
                                                    <b-link @click="extensionsEditMode = !extensionsEditMode"
                                                            class="text-primary"
                                                            v-b-tooltip.hover
                                                            title="Manually edit the allowed extensions">
                                                        <b-icon
                                                            :icon="extensionsEditMode ? 'x-octagon' : 'pencil'"></b-icon>
                                                    </b-link>
                                                </b-input-group-append>
                                            </b-input-group>
                                        </b-col>
                                    </b-form-row>

                                    <owners :owners="currentPortal.owners" :isLeadSpace="currentPortal.leadSpace"/>
                                    <steps :steps="currentPortal.steps" :currentPortal="currentPortal" :infoGroups="infoGroups"/>
                                    <!-- // Main configuration -->
                                </b-card-body>
                            </b-collapse>
                        </b-card>

                        <b-card no-body class="mb-1">
                            <b-card-header header-tag="header" class="p-1" role="tab">
                                <b-button block v-b-toggle.acc-notification variant="primary">Notification</b-button>
                            </b-card-header>
                            <b-collapse id="acc-notification" accordion="my-accordion" role="tabpanel">
                                <b-card-body>
                                    <b-row class="text-center" v-if="!currentPortal.notifications || Object.keys(currentPortal.notifications).length === 0">
                                        <b-col md="12">
                                            <b-button squared variant="primary" class="mr-2" @click="addNotifications">
                                                Add notifications
                                            </b-button>
                                        </b-col>
                                    </b-row>
                                    <!-- Notification -->
                                    <notification v-for="(notification, index) in currentPortal.notifications"
                                                  :key="index" :notification="notification"/>
                                    <!-- // Notification -->
                                </b-card-body>
                            </b-collapse>
                        </b-card>

                        <b-card no-body class="mb-1">
                            <b-card-header header-tag="header" class="p-1" role="tab">
                                <b-button block v-b-toggle.acc-customization variant="primary">Customization</b-button>
                            </b-card-header>
                            <b-collapse id="acc-customization" accordion="my-accordion" role="tabpanel"
                                        @show="showCustomization = true" @hidden="showCustomization = false">
                                <b-card-body v-if="showCustomization">
                                    <!-- Customization -->
                                    <b-form-row>
                                        <b-col md="3">
                                            <b-form-group label="Logo url" label-for="logoUrl">
                                                <b-form-input id="logoUrl" name="logoUrl"
                                                              v-model="currentPortal.logoUrl"/>
                                            </b-form-group>
                                            <div class="d-flex justify-content-center">
                                                <b-img style="max-height: 40px" v-if="currentPortal.logoUrl"
                                                       :src="currentPortal.logoUrl"></b-img>
                                            </div>
                                        </b-col>
                                        <b-col md="3">
                                            <b-form-group label="Favicon url" label-for="faviconUrl">
                                                <b-input-group>
                                                    <template v-slot:append v-if="currentPortal.faviconUrl">
                                                        <b-input-group-text>
                                                            <b-avatar square size="sm" text=" " variant="transparent"
                                                                      :src="currentPortal.faviconUrl"></b-avatar>
                                                        </b-input-group-text>
                                                    </template>
                                                    <b-form-input id="faviconUrl" name="faviconUrl"
                                                                  v-model="currentPortal.faviconUrl"/>
                                                </b-input-group>
                                            </b-form-group>
                                        </b-col>
                                        <b-col md="3">
                                            <b-form-group label="Submit Submission Button text"
                                                          label-for="submitSubmissionButton">
                                                <b-form-input id="submitSubmissionButton" name="submitSubmissionButton"
                                                              v-model="currentPortal.submitSubmissionButton"/>
                                            </b-form-group>
                                        </b-col>
                                        <b-col md="3">
                                            <b-form-group label="New Submission Button text"
                                                          label-for="newSubmissionButton">
                                                <b-form-input id="newSubmissionButton" name="newSubmissionButton"
                                                              v-model="currentPortal.newSubmissionButton"/>
                                            </b-form-group>
                                        </b-col>
                                    </b-form-row>

                                    <b-form-group label="Style (css)" label-for="style">
                                        <ace-editor id="style" name="style" v-model="currentPortal.style"
                                                    @init="editorInit" :options="editorOptions" lang="css"
                                                    theme="chrome" width="100%"></ace-editor>
                                    </b-form-group>

                                    <b-form-group label="Header (html)" label-for="header">
                                        <ace-editor id="header" name="header" v-model="currentPortal.header"
                                                    @init="editorInit" :options="editorOptions" lang="html"
                                                    theme="chrome" width="100%"></ace-editor>
                                    </b-form-group>

                                    <b-form-group label="Footer (html)" label-for="footer">
                                        <ace-editor id="footer" name="footer" v-model="currentPortal.footer"
                                                    @init="editorInit" :options="editorOptions" lang="html"
                                                    theme="chrome" width="100%"></ace-editor>
                                    </b-form-group>

                                    <b-form-group label="Confirmation message (html)" label-for="confirmationMessage">
                                        <ace-editor id="confirmationMessage" name="confirmationMessage"
                                                    v-model="currentPortal.confirmationMessage" @init="editorInit"
                                                    :options="editorOptions" lang="html" theme="chrome"
                                                    width="100%"></ace-editor>
                                    </b-form-group>

                                    <b-form-group label="Custom disabled message (html)" label-for="disabledMessage">
                                        <ace-editor id="disabledMessage" name="disabledMessage"
                                                    v-model="currentPortal.disabledMessage" @init="editorInit"
                                                    :options="editorOptions" lang="html" theme="chrome"
                                                    width="100%"></ace-editor>
                                    </b-form-group>
                                    <!-- // Customization -->
                                </b-card-body>
                            </b-collapse>
                        </b-card>

                        <b-card no-body class="mb-1">
                            <b-card-header header-tag="header" class="p-1" role="tab">
                                <b-button block v-b-toggle.acc-more-conf variant="primary">Additional configuration</b-button>
                            </b-card-header>
                            <b-collapse id="acc-more-conf" accordion="my-accordion" role="tabpanel">
                                <b-card-body>
                                    <!-- Maintenance Message -->
                                    <b-form-group label="Custom maintenance message (html)" label-for="maintenanceMessage">
                                        <ace-editor id="maintenanceMessage" name="maintenanceMessage"
                                                    v-model="currentPortal.maintenanceMessage" @init="editorInit"
                                                    :options="editorOptions" lang="html" theme="chrome"
                                                    width="100%"></ace-editor>
                                    </b-form-group>
                                    <!-- Blacklist group -->
                                    <b-row v-if="!currentPortal.leadSpace">
                                        <b-col sm="6" md="4">
                                            <b-form-group>
                                                <label for="blacklistGroup">Blacklist Contact Group
                                                    <b-link id="blacklistGroupHelp">
                                                        <b-icon icon="question-circle" variant="primary"></b-icon>
                                                    </b-link>
                                                    <b-popover target="blacklistGroupHelp" triggers="focus"
                                                               placement="top">
                                                        The Contact Group used to blacklist contact on the portal.
                                                    </b-popover>
                                                </label>

                                                <b-input-group v-if="groups || !currentPortal.blacklist" >
                                                    <b-form-select id="blacklistGroup" name="blacklistGroup"
                                                                   v-model="currentPortal.blacklist" :disabled="!groups">
                                                        <b-form-select-option v-for="(selectGroup, indexG) in groups"
                                                                              :key="indexG"
                                                                              :value="indexG">
                                                            {{ selectGroup }}
                                                        </b-form-select-option>
                                                    </b-form-select>
                                                    <b-input-group-append is-text>
                                                        <b-link @click="getContactGroups" class="text-primary"
                                                                v-b-tooltip.hover>
                                                            <b-icon icon="cloud-download"></b-icon>
                                                        </b-link>
                                                    </b-input-group-append>
                                                </b-input-group>

                                                <b-input-group v-if="currentPortal.blacklist && !groups">
                                                    <b-form-input id="blacklistGroup" name="blacklistGroup"
                                                                  v-model="currentPortal.blacklist" disabled/>
                                                    <b-input-group-append is-text>
                                                        <b-link @click="getContactGroups" class="text-primary"
                                                                v-b-tooltip.hover>
                                                            <b-icon icon="pencil"></b-icon>
                                                        </b-link>
                                                    </b-input-group-append>
                                                </b-input-group>
                                            </b-form-group>
                                        </b-col>
                                        <b-col sm="6" md="8">
                                            <b-form-group label="Custom blacklist message"
                                                          label-for="blacklistMessage">
                                                <b-form-input id="blacklistMessage" name="blacklistMessage"
                                                              v-model="currentPortal.blacklistMessage"/>
                                            </b-form-group>
                                        </b-col>
                                    </b-row>
                                </b-card-body>
                            </b-collapse>
                        </b-card>
                    </div>

                    <b-row class="my-3">
                        <b-col md="6" class="text-left">
                            <b-button squared variant="outline-primary" class="mr-2" @click="cancelPortal">
                                Cancel
                            </b-button>
                        </b-col>

                        <b-col md="6" class="text-right">
                            <b-button squared v-if="isNewPortal" type="submit" variant="primary">
                                <b-icon icon="check"></b-icon>
                                Create
                            </b-button>
                            <b-button squared v-if="(isManager || isAdmin) && !isNewPortal" type="submit"
                                      variant="primary" class="mr-2">
                                <b-icon icon="check"></b-icon>
                                Update
                            </b-button>
                            <b-button squared v-if="(isManager || isAdmin) && !isNewPortal" @click="publishPortal(currentPortal.name, currentPortal.published)"
                                      :variant="currentPortal.published ? 'secondary' : 'success'" class="mx-2">
                                <b-icon :icon="currentPortal.published ? 'arrow-counterclockwise' : 'cloud-arrow-up'"></b-icon>
                                {{ currentPortal.published ? 'Unpublish' : 'Publish'}}
                            </b-button>
                            <b-button squared v-if="isAdmin && !isNewPortal" @click="deletePortal" variant="danger"
                                      class="ml-2">
                                <b-icon icon="x"></b-icon>
                                Delete
                            </b-button>
                        </b-col>
                    </b-row>

                </b-form>

            </b-col>
        </b-row>

        <!-- Info modal -->
        <b-modal v-if="currentPortal" id="manageModal" title="Portal managed by" size="m" ok-only centered scrollable>

            <b-list-group v-if="currentPortal.managers">
                <b-list-group-item v-for="(manager, index) in currentPortal.managers" :key="index"
                                   class="d-flex align-items-center">
                    <b-icon icon="person-circle" scale="2" variant="secondary" class="mr-3"></b-icon>
                    <span class="mr-auto">{{ manager }}</span>
                    <b-link @click="removeManager(index)" class="h4 text-danger"
                            v-if="(isManager || isAdmin) && currentPortal.managers.length > 1">
                        <b-icon icon="x-circle"></b-icon>
                    </b-link>
                </b-list-group-item>
            </b-list-group>
            <b-input-group v-if="isManager || isAdmin" class="mt-3">
                <b-input-group-prepend is-text>
                    <b-icon icon="envelope"></b-icon>
                </b-input-group-prepend>
                <b-form-input id="newManager" name="newManager" type="email" v-model="newManager"/>
                <b-input-group-append>
                    <b-button variant="outline-primary" @click="addManager">
                        <b-icon icon="plus-circle"></b-icon>
                    </b-button>
                </b-input-group-append>
            </b-input-group>
        </b-modal>

        <b-modal id="modal-export" title="Export portal configuration" @ok="exportPortal($event, currentPortal.name)"
                 ok-title="Export" :ok-disabled="exportModal.loading" :cancel-disabled="exportModal.loading">
            <b-overlay :show="exportModal.loading">
                <b-form-group label="Export file name" label-for="export-fileName">
                    <b-input-group append=".json">
                        <b-form-input id="export-fileName" name="export-fileName"
                                      v-model="exportModal.fileName"></b-form-input>
                    </b-input-group>
                </b-form-group>
                <b-form-checkbox id="ckc-export-client" name="ckc-export-client" v-model="exportModal.withClient">
                    With client configuration
                </b-form-checkbox>
            </b-overlay>
        </b-modal>

        <b-modal id="modal-iframe" title="Iframe snippet" size="lg">
            <b-form-group label="Iframe html snippet" label-for="iframeHtml">
                <ace-editor id="iframeHtml" name="iframeHtml" @init="editorInit"
                            :options="editorOptions" lang="html" theme="chrome" width="100%"
                            v-model="iframeHtml">
                </ace-editor>
            </b-form-group>

            <b-form-group label="Iframe javascript snippet" label-for="iframeJS">
                <ace-editor id="iframeJS" name="iframeJS" @init="editorInit"
                            :options="editorOptions" lang="javascript" theme="chrome" width="100%"
                            v-model="iframeJS">
                </ace-editor>
            </b-form-group>

            <b-form-checkbox v-if="currentPortal" id="iframe" name="iframe" class="mt-2 ckc-switch-square" switch
                             v-model="currentPortal.iframe">
                Iframe style
            </b-form-checkbox>

        </b-modal>

        <b-modal id="modal-import" title="Import portal configuration" @ok="importPortal" size="lg"
                 ok-title="Import" :ok-disabled="!importModal.file">
            <b-form-group label="Import file" label-for="export-fileName">
                <b-form-file v-model="importModal.file" accept=".json"
                             placeholder="Select a portal json file or drop it here..."
                             drop-placeholder="Drop file here..."/>
            </b-form-group>
        </b-modal>

        <b-modal v-if="currentPortal" id="modal-disable" title="Please Confirm" ok-title="YES" cancel-title="NO"
                 size="lg"
                 @ok="disablePortal(currentPortal.name, currentPortal.disabled)">
            <b-form-group v-if="!currentPortal.disabled" label="Custom disabled message (html)"
                          label-for="modalDisabledMessage">
                <ace-editor id="modalDisabledMessage" name="modalDisabledMessage"
                            v-model="currentPortal.disabledMessage" @init="editorInit"
                            :options="editorOptions" lang="html" theme="chrome"
                            width="100%"></ace-editor>
            </b-form-group>

            <p class="text-center"><b>Are you sure you want to {{ currentPortal.disabled ? 'enable' : 'disable' }} the
                portal ?</b></p>
        </b-modal>
    </b-container>
</template>

<script>
    import PortalDataService from "@/services/PortalDataService";
    import ClientDataService from "@/services/ClientDataService";
    import LeadSpaceDataService from "@/services/LeadSpaceDataService";
    import Steps from "./portal/PortalSteps";
    import Owners from "./portal/PortalOwners";
    import Notification from "./portal/PortalNotification";
    import StringUtils from "@/services/utils/StringUtils";
    import EditorUtils from "@/services/utils/EditorUtils";
    import CacheService from "@/services/CacheService";

    export default {
        name: "Portal",
        mixins: [StringUtils, EditorUtils],
        components: {
            Steps,
            Owners,
            Notification
        },
        props: {
            isNewPortal: {
                type: Boolean,
                default: false
            },
            isDuplicate: {
                type: Boolean,
                default: false
            },
            client: {
                type: Object
            },
            id: {
                type: Number
            }
        },
        data() {
            return {
                currentPortal: null,
                nameChanged: false,
                clients: null,
                briefs: null,
                groups: null,
                selectedBrief: null,
                infoGroups: [],
                classNames: [
                    {value: 'Submission', text: 'Submission'},
                    {value: 'Product', text: 'Product'},
                    {value: 'Lead', text: 'Lead'},
                    {value: 'InternalDevelopmentProject', text: 'InternalDevelopmentProject'},
                    {value: 'InLicensingProject', text: 'InLicensingProject'},
                    {value: 'Agreement', text: 'Agreement'},
                    {
                        label: 'Deal',
                        options: [
                            {
                                value: 'LS-Lead',
                                text: 'Opportunities'
                            },
                        ]

                    },
                ],
                extensionsEditMode: false,
                selectedExtensions: [],
                extensions: [
                    {text: 'docx', value: '.docx'},
                    {text: 'doc', value: '.doc'},
                    {text: 'jpg', value: '.jpg'},
                    {text: 'jpeg', value: '.jpeg'},
                    {text: 'pdf', value: '.pdf'},
                    {text: 'png', value: '.png'},
                    {text: 'ppt', value: '.ppt'},
                    {text: 'pptx', value: '.pptx'},
                    {text: 'xls', value: '.xls'},
                    {text: 'xlsx', value: '.xlsx'}
                ],
                managersFields: [
                    {key: 'email', label: ''},
                    {key: 'edit', label: '', class: 'text-center align-middle col-icon'}
                ],
                newManager: '',
                exportModal: {
                    loading: false,
                    fileName: '',
                    withClient: false,
                },
                importModal: {
                    file: null,
                    portal: null,
                },
                showCustomization: false,
                iframeHtml: '',
                iframeJS: '',
            };
        },
        mounted() {
            if (this.isNewPortal) {
                if (this.isDuplicate) {
                    this.duplicate(this.id);
                } else {
                    let currentUser = this.$store.state.auth.user;
                    this.selectedExtensions = this.extensions.map(ext => ext.value);
                    this.currentPortal = {
                        client: this.client,
                        owners: [{owner: true}],
                        className: 'Submission',
                        steps: [{position: 1, fields: []}],
                        accessCode: 'text10',
                        notifications: {
                            'OWNER': {type: 'OWNER', active: true},
                            'SUBMITTER': {type: 'SUBMITTER', active: true}
                        },
                        managers: [currentUser.email],
                        allowedExtensions: this.selectedExtensions.join(', '),
                        captchaCheck: true,
                        published: false
                    };
                }
                this.getClients();
            } else {
                this.getPortal(this.$route.params.name);
            }
        },
        computed: {
            isAdmin() {
                return this.$store.getters.isAdmin;
            },
            isManager() {
                if (!this.isNewPortal) {
                    return this.$store.getters.isManager(this.currentPortal);
                } else {
                    return true;
                }
            }
        },
        methods: {
            getPortal(name) {
                CacheService.clearPortal(name, false)
                    .then(response => {
                        if (response) {
                            PortalDataService.get(name)
                                .then(response => {
                                    this.currentPortal = response.data;
                                    this.selectedExtensions = this.currentPortal.allowedExtensions.split(', ');
                                    this.selectedBrief = this.currentPortal.brief;

                                    if (this.currentPortal.leadSpace) {
                                        this.getInfoGroups();
                                    }

                                    this.iframeSetup();
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$router.push({name: "ConfPortals"});
                                });
                        }
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            iframeSetup() {
                this.iframeHtml = "<iframe src='urlToPortal' id='oppIframe' frameborder=\"0\" width=\"100%\"  scrolling=\"no\"></iframe>";
                this.iframeJS = "// Listen for messages sent from the iFrame\n" +
                    "var eventMethod = window.addEventListener ? \"addEventListener\" : \"attachEvent\";\n" +
                    "var eventer = window[eventMethod];\n" +
                    "var messageEvent = eventMethod == \"attachEvent\" ? \"onmessage\" : \"message\";\n" +
                    "\n" +
                    "eventer(messageEvent,function(e) {\n" +
                    "   // If the message is a resize frame request\n" +
                    "   if (e.data.indexOf('resize::') != -1) {\n" +
                    "       var height = e.data.replace('resize::', '');\n" +
                    "       document.getElementById('oppIframe').style.height = height+'px';\n" +
                    "   }\n" +
                    "} ,false);";
            },
            duplicate(id) {
                PortalDataService.duplicate(id)
                    .then(response => {
                        this.currentPortal = response.data;
                        this.selectedExtensions = this.currentPortal.allowedExtensions.split(', ');
                        let currentUser = this.$store.state.auth.user;
                        this.currentPortal.managers = [currentUser.email];
                        this.currentPortal.published = false;
                    })
                    .catch(error => console.log(error))
            },
            onSubmit(evt) {
                if (!this.extensionsEditMode) {
                    this.currentPortal.allowedExtensions = this.selectedExtensions.join(', ');
                }
                if (this.isNewPortal) {
                    this.savePortal();
                } else {
                    this.updatePortal();
                }
                evt.preventDefault();
            },
            savePortal() {
                this.$bvModal.show('loading-modal');
                PortalDataService.create(this.currentPortal)
                    .then(response => {
                        this.$parent.displaySuccessMessage('The portal was created successfully.');
                        this.$router.push({name: "ConfPortal", params: {name: response.data.name}});
                    })
                    .catch(error => {
                        console.log(error);
                        this.$parent.displayErrorMessage('An error occurred while trying to create the portal.');
                    });
            },
            updatePortal() {
                this.$bvModal.msgBoxConfirm('Are you sure you want to update the portal ?', {
                    title: 'Please Confirm',
                    okVariant: 'success',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            PortalDataService.update(this.currentPortal.id, this.currentPortal)
                                .then(response => {
                                    this.currentPortal = response.data;
                                    this.$parent.displaySuccessMessage('The portal was updated successfully.');
                                    // this.$router.push({name: "ConfPortal", params: {name: response.data.name}});
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$parent.displayErrorMessage('An error occurred while trying to updated the portal.');
                                });
                        }
                    })
                    .catch(error => console.log(error));
            },
            deletePortal() {
                this.$bvModal.msgBoxConfirm('Are you sure you want to delete the portal ?', {
                    title: 'Please Confirm',
                    okVariant: 'danger',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            PortalDataService.delete(this.currentPortal.name)
                                .then(response => {
                                    console.log(response);
                                    this.$parent.displaySuccessMessage('The portal was deleted successfully.');
                                    this.$router.push({name: "ConfPortals"});
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$parent.displayErrorMessage('An error occurred while trying to delete the portal.');
                                });
                        }
                    })
                    .catch(error => console.log(error));
            },
            getClients() {
                ClientDataService.getAll()
                    .then(response => (this.clients = response.data))
                    .catch(error => console.log(error))
            },
            getBriefs() {
                LeadSpaceDataService.getBriefs(this.currentPortal.client.id)
                    .then(response => (this.briefs = response.data))
                    .catch(error => console.log(error))
            },
            getContactGroups() {
                ClientDataService.findContactGroups(this.currentPortal.client.id)
                    .then(response => (this.groups = response.data))
                    .catch(error => console.log(error))
            },
            getInfoGroups() {
                LeadSpaceDataService.findInfoGroups(this.currentPortal.client.id)
                    .then(response => {
                        this.infoGroups = response.data
                    })
                    .catch(error => console.log(error))
            },
            selectClient() {
                if (this.currentPortal.leadSpace) {
                    // Clear initiative
                    this.currentPortal.brief = null;
                    this.currentPortal.briefName = "";
                    this.currentPortal.assetType = "";
                    this.getBriefs();
                    this.getInfoGroups();
                }
            },
            selectBrief() {
                let id = this.selectedBrief;
                var briefObj = this.briefs.filter(function(brief){
                    if(brief.dobjId === id) return brief;
                });

                this.currentPortal.brief = this.selectedBrief;
                this.currentPortal.briefName = briefObj[0].name;
                // TODO select asset type
                this.currentPortal.assetType = briefObj[0].allowedAssetTypes[0];
            },
            clearPortalCache(name) {
                this.$bvModal.msgBoxConfirm('Are you sure you want to refresh the portal cache ?', {
                    title: 'Please Confirm',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            CacheService.clearPortal(name, true)
                                .then(response => {
                                    if (response) {
                                        this.$parent.displaySuccessMessage('The cache was deleted cleared.');
                                    }
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$parent.displayErrorMessage('An error occurred while trying to clear the cache.');
                                });
                        }
                    })
                    .catch(error => console.log(error));
            },
            disablePortal(name, disabled) {
                this.$bvModal.show('loading-modal');
                PortalDataService.disable(this.currentPortal.id, name, !disabled, this.currentPortal.disabledMessage)
                    .then(response => {
                        if (response) {
                            this.currentPortal = response.data;
                            this.$parent.displaySuccessMessage(`The portal was successfully ${disabled ? 'enabled' : 'disabled'}.`);
                        }
                    })
                    .catch(error => {
                        console.log(error);
                        this.$parent.displayErrorMessage(`An error occurred while trying to ${disabled ? 'enable' : 'disable'} the portal.`);
                    });
            },
            publishPortal(name, published) {
                this.$bvModal.msgBoxConfirm(`Are you sure you want to ${published ? 'unpublished' : 'published'} the portal ?`, {
                    title: 'Please Confirm',
                    okVariant: 'success',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            this.currentPortal.published = !this.currentPortal.published;
                            PortalDataService.update(this.currentPortal.id, this.currentPortal)
                                .then(response => {
                                    this.currentPortal = response.data;
                                    this.$parent.displaySuccessMessage(`The portal was successfully ${published ? 'unpublished' : 'published'}.`);
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$parent.displayErrorMessage(`An error occurred while trying to ${published ? 'unpublished' : 'published'} the portal.`);
                                });
                        }
                    })
                    .catch(error => console.log(error));
            },
            duplicatePortal() {
                this.$bvModal.msgBoxConfirm('Are you sure you want to duplicate this portal ?', {
                    title: 'Please Confirm',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$router.push({
                                name: 'ConfNewPortal',
                                params: {isNewPortal: true, isDuplicate: true, id: this.currentPortal.id}
                            });
                        }
                    })
                    .catch(error => console.log(error));
            },
            cancelPortal() {
                this.$bvModal.msgBoxConfirm('Are you sure you want to cancel the edit to this portal ?', {
                    title: 'Please Confirm',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$router.push({name: 'ConfPortals'});
                        }
                    })
                    .catch(error => console.log(error));
            },
            getManagerId(index, attribute) {
                return 'manager' + index + '-' + attribute;
            },
            addManager() {
                if (!this.isEmpty(this.newManager)) {
                    this.currentPortal.managers.push(this.newManager);
                    this.newManager = '';
                }
            },
            removeManager(index) {
                this.currentPortal.managers.splice(index, 1)
            },
            showExportModal() {
                this.exportModal.loading = false;
                this.exportModal.fileName = 'portal-export-' + this.currentPortal.name;
                this.$root.$emit('bv::show::modal', 'modal-export')
            },
            showImportModal() {
                this.importModal.portal = null;
                this.$root.$emit('bv::show::modal', 'modal-import')
            },
            importPortal(event) {
                event.preventDefault();
                this.$bvModal.msgBoxConfirm('Are you sure you want to import the configuration into the portal ?', {
                    title: 'Please Confirm',
                    okVariant: 'danger',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            const reader = new FileReader();
                            reader.onload = event => {
                                this.importModal.portal = JSON.parse(event.target.result);
                                this.importModal.portal.id = this.currentPortal.id;
                                this.importModal.portal.version = this.currentPortal.version;
                                this.importModal.portal.client = this.currentPortal.client;
                                this.importModal.portal.name = this.currentPortal.name;
                                this.currentPortal = this.importModal.portal;
                                this.$root.$emit('bv::hide::modal', 'modal-import')
                                this.$parent.displaySuccessMessage('The portal was imported successfully.');
                            };
                            reader.readAsText(this.importModal.file);
                        }
                    })
                    .catch(error => console.log(error));
            },
            exportPortal(event, name) {
                event.preventDefault();
                this.exportModal.loading = true;
                PortalDataService.export(name, this.exportModal.withClient)
                    .then(response => {
                        this.downloadString(JSON.stringify(response.data), "text/json", this.exportModal.fileName + ".json")
                        this.$nextTick(() => {
                            this.$bvModal.hide('modal-export')
                            this.exportModal.loading = false;
                        })
                    })
                    .catch(error => console.log(error))
            },
            selectClass() {
                if (this.currentPortal.className.startsWith('LS-')) {
                    this.currentPortal.leadSpace = true;
                } else {
                    this.currentPortal.leadSpace = false;
                }
            },
            addNotifications() {
                this.currentPortal.notifications = {
                    'OWNER': {type: 'OWNER', active: true},
                    'SUBMITTER': {type: 'SUBMITTER', active: true}
                };
            }
        },
        updated: function () {
            this.$nextTick(function () {
                const dropdown = document.getElementById('portal-dropdown');
                if (dropdown) {
                    dropdown.getElementsByTagName("a").item(0).target = "_blank";
                }
            })
        }
    }
</script>

<style>
    .col-icon {
        width: 50px;
    }

    .col-ckc {
        width: 78px;
    }
</style>